import { canUseDOM } from 'exenv';
import loadable from '@loadable/component';
import _ from 'lodash';
import {
  FC,
  PropsWithChildren,
  ReactElement,
  createElement,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { useLocation } from 'react-router';
import Sticky from 'react-stickynode';
import frontendLog from '../../libs/frontendLog';
import { ensure } from '../../../common/utils';
import { useIsClient } from '../../scripts/hooks/useIsClient';
import { useCurrentUserId, useFeatures, useSiteConfig } from '../../scripts/selectors/global';
import {
  PW_HACK_MAP_SELECTOR_ID_TO_UNIT_ID,
  destroyAditudeSlots,
  queueAditudeZone
} from '../../services/advertisingService';
import { asset } from '../../services/assetResolverService';
import Skeleton from '../presentational/skeleton';
import { ZoneBlockerPlea, ZoneBlockerPleaProps } from '../zoneBlockerPlea';
import { useAdblockDetection } from '../adblock/useAdblockDetection';
import { InStreamVideoContext } from './instreamVideoContext';
import { buildMediavineHintSizes } from './mediavineHintSizes';

const FreestarAdSlot = loadable(() => import('@freestar/pubfig-adslot-react-component'), {
  fallback: null
});

const BASE_ADSENSE_CONFIG = { 'data-ad-client': 'ca-pub-3615139078495097' };

export const FORCE_NITROPAY_DEMO_MODE = false;

const ADITUDE_HORIZONTAL_SIZES = {
  desktop: '970x90,728x90,468x60',
  mobile: '336x280,300x250,320x100,320x50,300x50'
};

const ADITUDE_SIDEBAR_SIZES = {
  desktop: '300x600,300x400,300x250,250x250,300x200,300x50,160x600,120x600'
};

/** Ad providers that support in-article/in-content ad placements we manage (not injected dynamically by the provider) */
export const IN_ARTICLE_AD_PROVIDERS = ['playwire', 'nitropay', 'mediavine', 'aditude'];

export type TZoneType = keyof typeof ZONE_DEFINITIONS;

export type TDisplayAdsProvider =
  | 'playwire'
  | 'venatus'
  | 'freestar'
  | 'nitropay'
  | 'adsense'
  | 'tds'
  | 'mediavine'
  | 'aditude'
  | 'none';
export type TVideoAdsProvider =
  | 'playwire'
  | 'venatus'
  | 'anyclip'
  | 'anyclip_outstream'
  | 'exco'
  | 'nitropay'
  | 'tds'
  | 'mediavine'
  | 'aditude'
  | 'connatix'
  | 'primis'
  | 'none';

export type TZoneProps = {
  type: TZoneType;
  destroyOnUnmount?: boolean;
  hidden?: boolean;
  sticky?: boolean;
};

type TZoneContainerProps = {
  className?: string;
  destroyOnUnmount?: boolean;
  tag?: string;
  sticky?: boolean;
  type: TZoneType;
};

type TMediaVineConfig = {
  slot:
    | 'Comments'
    | 'Content'
    | 'Feed'
    | 'Interstitial'
    | 'Leaderboard'
    | 'LeaderboardAtf'
    | 'LeaderboardBtf'
    | 'Recipe'
    | 'SidebarAtf'
    | 'SidebarBtf'
    | 'SidebarBtfStacked'
    | 'GutterAtf'
    | 'Arrival';
  sizeMode?: 'auto' | 'manual';
  sizes?: string;
};

type TAditudeConfig = {
  baseDivId: string;
  sizes?: { desktop?: string; mobile?: string };
};

type TZoneProviderDefinition = {
  providerSource?: 'display' | 'video';
  units: []; // deprecated - REMOVE
  hidden?: boolean;
  skipTag?: boolean;
  freestarUnitId: string;
  playwireUnitId: string;
  venatusUnitId: string;
  nitropayUnitId?: string;
  nitropayConfig?: any;
  adsenseConfig?: any;
  tdsConfig?: any;
  mediavineConfig?: TMediaVineConfig;
  aditudeConfig?: TAditudeConfig;
  egUnitId: string;
  sizes: number[][];
  sizeOverrides?: { [key: string]: number[][] };
  providerOverrides?: any;
  defaultUrl: string;
  destroyOnUnmount?: boolean;
  zoneBlocker?: ZoneBlockerPleaProps;
  zoneBlockerGroup?: string;
  zoneBlockerPriority?: number;
};

let uniqueZoneId = 0;
let refreshHandle: NodeJS.Timeout | null = null;
let uniqueZoneBlockerRegistrationId = 0;

const zoneBlockerRegistry = new Map<string, Map<string, number>>();
const zoneBlockerRegistryListeners = new Set<() => void>();

function emitZoneBlockerRegistryChange() {
  zoneBlockerRegistryListeners.forEach((listener) => listener());
}

function subscribeToZoneBlockerRegistry(listener: () => void) {
  zoneBlockerRegistryListeners.add(listener);
  return () => zoneBlockerRegistryListeners.delete(listener);
}

function getHighestZoneBlockerPriority(group?: string): number | null {
  if (!group) {
    return null;
  }

  const entries = zoneBlockerRegistry.get(group);
  if (!entries || entries.size === 0) {
    return null;
  }

  return Math.max(...entries.values());
}

function registerZoneBlockerCandidate(group: string, id: string, priority: number) {
  let entries = zoneBlockerRegistry.get(group);
  if (!entries) {
    entries = new Map();
    zoneBlockerRegistry.set(group, entries);
  }

  entries.set(id, priority);
  emitZoneBlockerRegistryChange();

  return () => {
    const currentEntries = zoneBlockerRegistry.get(group);
    if (!currentEntries) {
      return;
    }

    currentEntries.delete(id);
    if (currentEntries.size === 0) {
      zoneBlockerRegistry.delete(group);
    }

    emitZoneBlockerRegistryChange();
  };
}

function useShouldRenderZoneBlockerPlea(zone: TZoneProviderDefinition | undefined, adsBlocked: boolean) {
  const group = zone?.zoneBlockerGroup;
  const priority = zone?.zoneBlockerPriority;
  const [registrationId] = useState(() => `rio-zone-blocker-${++uniqueZoneBlockerRegistrationId}`);

  const highestPriority = useSyncExternalStore(
    subscribeToZoneBlockerRegistry,
    () => getHighestZoneBlockerPriority(group),
    () => null
  );

  useLayoutEffect(() => {
    if (!adsBlocked || !zone?.zoneBlocker || !group || priority === undefined) {
      return;
    }

    return registerZoneBlockerCandidate(group, registrationId, priority);
  }, [adsBlocked, group, priority, registrationId, zone]);

  if (!zone?.zoneBlocker) {
    return false;
  }

  if (!group || priority === undefined || highestPriority === null) {
    return true;
  }

  return priority >= highestPriority;
}

export const ZoneContainer: FC<PropsWithChildren<TZoneContainerProps>> = (props) => {
  const currentFeatures = useFeatures();
  const userId = useCurrentUserId();
  const siteConfig = useSiteConfig();
  const { displayAdsProvider, videoAdsProvider } = siteConfig as {
    displayAdsProvider: TDisplayAdsProvider;
    videoAdsProvider: TVideoAdsProvider;
  };
  const [visible, setVisible] = useState(() => !currentFeatures.no_ads);
  const { isBlocked: adsBlocked } = useAdblockDetection();
  const zoneDef = ZONE_DEFINITIONS[props.type];
  const adsProvider = zoneDef?.providerSource === 'video' ? videoAdsProvider : displayAdsProvider;
  const shouldRenderZoneBlockerPlea = useShouldRenderZoneBlockerPlea(zoneDef, Boolean(adsBlocked));

  useEffect(() => {
    setVisible(!currentFeatures.no_ads);
  }, [currentFeatures.no_ads]);

  if (!visible) {
    return null;
  }

  if (adsBlocked) {
    // When ads are blocked, provider visibility is irrelevant.
    // Only keep zones that have a blocker plea configured.
    if (!zoneDef?.zoneBlocker || !shouldRenderZoneBlockerPlea) {
      return null;
    }
  } else {
    // When ads are serving, respect provider-specific zone hiding
    if (zoneDef?.providerOverrides?.[adsProvider]?.hidden) {
      return null;
    }
  }

  const zone = <Zone key="0" type={props.type} sticky={props.sticky} destroyOnUnmount={props.destroyOnUnmount} />;
  const content = createElement(props.tag || 'div', { key: props.type, className: props.className }, [zone]);

  if (props.sticky) {
    return (
      <Sticky
        top={userId ? '.login-bar' : 10}
        bottomBoundary=".rio-content-pane"
        className="rio-sidebar-section rio-sidebar-section--nobg"
      >
        {content}
      </Sticky>
    );
  }

  return content;
};

const ZONE_DEFINITIONS: Record<string, TZoneProviderDefinition> = {
  bottom_rail: {
    providerOverrides: {
      playwire: { hidden: true },
      nitropay: { skipTag: true }
    },
    units: [],
    freestarUnitId: '',
    playwireUnitId: 'bottom_rail',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: 'bottom_rail',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      format: 'anchor-v2',
      anchor: 'bottom',
      anchorBgColor: 'rgb(0 0 0 / 0%)',
      anchorClose: false,
      anchorPersistClose: false,
      anchorStickyOffset: 0,
      mediaQuery: '(min-width: 0px)',
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'top-right' }
    },
    mediavineConfig: { slot: 'LeaderboardBtf', sizeMode: 'manual', sizes: '728x90,970x90' },
    aditudeConfig: { baseDivId: 'pb-slot-bottom_rail', sizes: { desktop: ADITUDE_HORIZONTAL_SIZES.desktop } },
    sizeOverrides: { aditude: [[970, 90]] },
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  playwire_flex_leaderboard: {
    hidden: true,
    units: [],
    freestarUnitId: '',
    playwireUnitId: 'flex_leaderboard',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: '',
    nitropayConfig: {},
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  left_rail: {
    providerOverrides: {
      playwire: { hidden: true },
      nitropay: { skipTag: true }
    },
    units: [],
    freestarUnitId: '',
    playwireUnitId: 'left_rail',
    venatusUnitId: '',
    egUnitId: '',
    tdsConfig: { plId: '11170', prId: '11170-30', product: 'sidest' },
    nitropayUnitId: 'left_rail',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      format: 'rail',
      rail: 'left',
      mediaQuery: '(min-width: 2080px)',
      railOffsetTop: 0,
      railOffsetBottom: 0,
      railCollisionWhitelist: [
        '#application',
        '#reamazejs-container'
        // '#rio-body',
        // '.slds-container--center',
        // '.sticky-outer-wrapper',
        // '.sticky-inner-wrapper',
        // '.rio-docked-video'
      ],
      railCloseColor: '#666666',
      railSpacing: 10,
      railStack: false,
      railStickyTop: 0,
      railVerticalAlign: 'center',
      sizes: [
        ['160', '600'],
        ['300', '600'],
        ['300', '250'],
        ['320', '50'],
        ['320', '100'],
        ['320', '480']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'top-right' }
    },
    mediavineConfig: { slot: 'GutterAtf', sizeMode: 'manual', sizes: '160x600,300x600,300x250' },
    aditudeConfig: { baseDivId: 'pb-slot-left_edge_ad', sizes: ADITUDE_SIDEBAR_SIZES },
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  left_edge_ad: {
    providerOverrides: {
      playwire: { hidden: true },
      venatus: { hidden: true },
      nitropay: { hidden: true },
      adsense: { hidden: true },
      tds: { hidden: true },
      freestar: { hidden: true }
    },
    units: [],
    freestarUnitId: '',
    playwireUnitId: '',
    venatusUnitId: '',
    egUnitId: '',
    mediavineConfig: { slot: 'SidebarAtf', sizeMode: 'auto' },
    aditudeConfig: { baseDivId: 'pb-slot-left_edge_ad', sizes: ADITUDE_SIDEBAR_SIZES },
    sizes: [[300, 600]],
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!
  },
  right_edge_ad: {
    providerOverrides: {
      playwire: { hidden: true },
      venatus: { hidden: true },
      nitropay: { hidden: true },
      adsense: { hidden: true },
      tds: { hidden: true },
      freestar: { hidden: true }
    },
    units: [],
    freestarUnitId: '',
    playwireUnitId: '',
    venatusUnitId: '',
    egUnitId: '',
    mediavineConfig: { slot: 'SidebarAtf', sizeMode: 'auto' },
    aditudeConfig: { baseDivId: 'pb-slot-right_edge_ad', sizes: ADITUDE_SIDEBAR_SIZES },
    sizes: [[300, 600]],
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!
  },
  nitro_video_instream: {
    providerSource: 'video',
    skipTag: true,
    units: [],
    freestarUnitId: '',
    playwireUnitId: '',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: 'rio-docked-video',
    nitropayConfig: {
      format: 'video-nc',
      video: {},
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'top-left'
      },
      geoDeny: ['CN', 'SG', 'IR', 'ID']
    },
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  nitro_video_outstream: {
    providerSource: 'video',
    skipTag: true,
    units: [],
    freestarUnitId: '',
    playwireUnitId: '',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: 'rio-docked-video',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      format: 'floating',
      video: {},
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'top-left'
      },
      geoDeny: ['CN', 'SG', 'IR', 'ID']
    },
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  nitro_in_content_desktop: {
    units: [],
    freestarUnitId: '',
    playwireUnitId: '',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: 'in_content_desktop',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['728', '90'],
        ['970', '90'],
        ['300', '250'],
        ['970', '250']
      ],
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'bottom-right'
      }
    },
    adsenseConfig: {},
    tdsConfig: {},
    sizeOverrides: {},
    sizes: [
      [728, 90],
      [468, 60]
    ],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!
  },
  nitro_in_content_mobile: {
    units: [],
    freestarUnitId: '',
    playwireUnitId: '',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: 'in_content_mobile',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['300', '250'],
        ['320', '100'],
        ['320', '480'],
        ['300', '600'],
        ['336', '280']
      ],
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'bottom-right'
      }
    },
    adsenseConfig: {},
    tdsConfig: {},
    sizeOverrides: {},
    sizes: [
      [728, 90],
      [468, 60]
    ],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!
  },
  playwire_sticky_sidebar: {
    hidden: true,
    units: [],
    freestarUnitId: '',
    playwireUnitId: 'sticky_sidebar',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: '',
    nitropayConfig: {},
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  playwire_video: {
    providerSource: 'video',
    hidden: true,
    units: [],
    freestarUnitId: '',
    playwireUnitId: 'corner_ad_video',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: '',
    nitropayConfig: {},
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  playwire_site_skin: {
    hidden: true,
    units: [],
    freestarUnitId: '',
    playwireUnitId: 'site_skin',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: '',
    nitropayConfig: {},
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  playwire_in_content: {
    units: [],
    freestarUnitId: '',
    playwireUnitId: 'in_content',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: '',
    nitropayConfig: {},
    sizes: [[0, 0]],
    defaultUrl: ''
  },
  tds_in_content: {
    units: [],
    freestarUnitId: '',
    playwireUnitId: '',
    venatusUnitId: '',
    egUnitId: '',
    nitropayUnitId: '',
    nitropayConfig: {},
    tdsConfig: { plId: '11170', prId: '11170-199', product: 'pau' },
    sizeOverrides: { tds: [[300, 250]] },
    sizes: [[600, 250]],
    defaultUrl: ''
  },
  venatus_mobile_in_article: {
    units: [],
    playwireUnitId: '',
    freestarUnitId: '',
    venatusUnitId: '5e1c44c7f832ac0701eb0d1d', // 300x50 Dynamic Mobile - 300x50, 320x50
    egUnitId: '',
    nitropayUnitId: '',
    nitropayConfig: {},
    sizes: [[300, 50]],
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!
  },
  horizontal_inlist: {
    units: [],
    freestarUnitId: 'raiderio_728x90_468x60_inlist',
    playwireUnitId: 'in_content',
    venatusUnitId: '5e1c4480ddd4900915beaa16', // 728x90 Static - 728x90
    egUnitId: 'div-gpt-ad-rdr-9',
    nitropayUnitId: 'in_article',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['728', '90'],
        ['970', '90'],
        ['300', '250'],
        ['970', '250']
      ],
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'bottom-right'
      }
    },
    adsenseConfig: { 'data-ad-slot': '7844992273', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-199', product: 'pau' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: {
      baseDivId: 'pb-slot-horizontal_inlist',
      sizes: ADITUDE_HORIZONTAL_SIZES
    },
    sizeOverrides: { tds: [[300, 250]] },
    sizes: [
      [728, 90],
      [468, 60]
    ],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!,
    zoneBlocker: {
      width: 728,
      height: 90,
      layout: 'banner'
    }
  },
  horizontal_inlist2: {
    units: [],
    freestarUnitId: 'raiderio_728x90_468x60_inlist_2',
    playwireUnitId: 'in_content',
    venatusUnitId: '5e1c4480ddd4900915beaa16', // 728x90 Static - 728x90
    egUnitId: 'div-gpt-ad-rdr-9',
    nitropayUnitId: 'in_article2',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      format: 'display',
      articleOffsetTop: 0,
      pageInterval: 1,
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'bottom-right'
      }
    },
    adsenseConfig: { 'data-ad-slot': '4681177857', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: {
      baseDivId: 'pb-slot-horizontal_inlist2',
      sizes: ADITUDE_HORIZONTAL_SIZES
    },
    sizes: [
      [728, 90],
      [468, 60]
    ],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!,
    zoneBlocker: {
      width: 728,
      height: 90,
      layout: 'banner'
    }
  },
  horizontal_inlist3: {
    units: [],
    freestarUnitId: 'raiderio_728x90_468x60_inlist_3',
    playwireUnitId: 'in_content',
    venatusUnitId: '5e1c4480ddd4900915beaa16', // 728x90 Static - 728x90
    egUnitId: 'div-gpt-ad-rdr-9',
    nitropayUnitId: 'in_article3',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      format: 'display',
      articleOffsetTop: 0,
      pageInterval: 1,
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'bottom-right'
      }
    },
    adsenseConfig: { 'data-ad-slot': '5820366681', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: {
      baseDivId: 'pb-slot-horizontal_inlist3',
      sizes: ADITUDE_HORIZONTAL_SIZES
    },
    sizes: [
      [728, 90],
      [468, 60]
    ],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!,
    zoneBlocker: {
      width: 728,
      height: 90,
      layout: 'banner'
    }
  },
  header: {
    units: [],
    freestarUnitId: 'raiderio_728x90_970x90_970x250_atf',
    playwireUnitId: 'leaderboard_atf',
    venatusUnitId: '5e1c44732b229654df07a47e', // 728x90 Dynamic - 728x90, 970x250, 970x90
    egUnitId: 'div-gpt-ad-rdr-9',
    nitropayUnitId: 'top_header',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['970', '90'],
        ['970', '250'],
        ['728', '90']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '9158073948', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: { baseDivId: 'pb-slot-header', sizes: { desktop: ADITUDE_HORIZONTAL_SIZES.desktop } },
    sizes: [
      [970, 250],
      [728, 90]
    ],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!,
    zoneBlocker: {
      width: 728,
      height: 90,
      layout: 'banner'
    }
  },
  mobile_header: {
    units: [],
    playwireUnitId: 'leaderboard_atf',
    freestarUnitId: 'raiderio_320x50_mobile',
    venatusUnitId: '5e1c44c7f832ac0701eb0d1d', // 300x50 Dynamic Mobile - 300x50, 320x50
    egUnitId: 'div-gpt-ad-rdr-1',
    nitropayUnitId: 'mobile_top_header',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['320', '50'],
        ['320', '100'],
        ['336', '280'],
        ['300', '250']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '9158073948', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-202', product: 'pau' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: { baseDivId: 'pb-slot-mobile_header', sizes: { mobile: ADITUDE_HORIZONTAL_SIZES.mobile } },
    sizes: [[300, 250]], // Reserve 250px height to prevent CLS when larger ads load
    sizeOverrides: { tds: [[300, 250]], aditude: [[300, 100]] },
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!
  },
  mobile_footer: {
    units: [],
    playwireUnitId: 'bottom_rail',
    freestarUnitId: '',
    venatusUnitId: '64ca7b377e562712d6a338b4', // 300x600 as per Abhi
    egUnitId: '',
    nitropayUnitId: 'mobile-anchor',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['320', '50'],
        ['320', '100'],
        ['336', '280'],
        ['300', '250']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '9017391576', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-207', product: 'pau' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: { baseDivId: 'pb-slot-mobile_footer', sizes: { mobile: '320x100,320x50,300x50' } },
    sizes: [[300, 50]],
    sizeOverrides: { aditude: [[320, 100]], tds: [[300, 250]] },
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!
  },
  footer: {
    units: [],
    playwireUnitId: 'leaderboard_btf',
    freestarUnitId: 'raiderio_728x90_970x90_970x250_btf',
    venatusUnitId: '5e1c44732b229654df07a47e', // 728x90 Dynamic - 728x90, 970x250, 970x90
    egUnitId: 'div-gpt-ad-rdr-10',
    nitropayUnitId: 'footer',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['970', '90'],
        ['970', '250'],
        ['728', '90']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '9017391576', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-201', product: 'pau' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: { baseDivId: 'pb-slot-footer', sizes: { desktop: '970x250,970x90,728x90,468x60' } },
    sizes: [
      [728, 90],
      [970, 90]
    ],
    sizeOverrides: {
      tds: [[300, 250]],
      mediavine: [[970, 250]]
    },
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!,
    zoneBlocker: {
      width: 728,
      height: 90,
      layout: 'banner'
    }
  },
  character_page_embedded: {
    units: [],
    playwireUnitId: 'leaderboard_btf',
    freestarUnitId: 'raiderio_728x90_970x90_970x250_btf',
    venatusUnitId: '5e1c4480ddd4900915beaa16', // 728x90 Dynamic - 728x90, 970x250, 970x90
    egUnitId: 'div-gpt-ad-rdr-10',
    nitropayUnitId: 'footer',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['970', '90'],
        ['970', '250'],
        ['728', '90']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '9017391576', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    sizes: [
      [728, 90],
      [970, 90]
    ],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!
  },
  mrec_sidebar_top_short: {
    units: [],
    playwireUnitId: 'sky_atf',
    freestarUnitId: 'raiderio_300x600_300x250_Right',
    venatusUnitId: '5e1c4447ddd4900915beaa12', // 300x250 Dynamic - 300x250, 300x600, 160x600
    egUnitId: 'div-gpt-ad-rdr-1',
    nitropayUnitId: 'sidebar_top',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['300', '600'],
        ['160', '600'],
        ['300', '250']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '3359237766', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-197', product: 'pau' },
    aditudeConfig: { baseDivId: 'pb-slot-sidebar_top_short', sizes: ADITUDE_SIDEBAR_SIZES },
    sizes: [[300, 250]],
    sizeOverrides: { playwire: [[300, 600]], tds: [[300, 600]] },
    providerOverrides: { mediavine: { hidden: true } },
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!,
    zoneBlocker: { width: 300, height: 445, layout: 'portrait' },
    zoneBlockerGroup: 'sidebar-tall',
    zoneBlockerPriority: 1
  },
  mrec_video_companion: {
    units: [],
    playwireUnitId: 'sky_atf',
    freestarUnitId: 'raiderio_300x600_300x250_Right',
    venatusUnitId: '5e1c4447ddd4900915beaa12', // 300x250 Dynamic - 300x250, 300x600, 160x600
    egUnitId: 'div-gpt-ad-rdr-1',
    nitropayUnitId: 'sidebar_top',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [['300', '250']],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '3359237766', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-197', product: 'pau' },
    mediavineConfig: { slot: 'Content', sizeMode: 'manual', sizes: '300x250' },
    aditudeConfig: { baseDivId: 'pb-slot-video_companion', sizes: { desktop: '300x250' } },
    sizes: [[300, 250]],
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!,
    zoneBlocker: { width: 300, height: 250, layout: 'portraitCompact' }
  },
  mrec_sidebar_bottom: {
    units: [],
    playwireUnitId: 'med_rect_atf',
    freestarUnitId: 'raiderio_300x600_300x250_Right_2',
    venatusUnitId: '5e1c4447ddd4900915beaa12', // 300x250 Dynamic - 300x250, 300x600, 160x600
    egUnitId: 'div-gpt-ad-rdr-8',
    nitropayUnitId: 'sidebar_bottom',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['300', '600'],
        ['160', '600'],
        ['300', '250']
      ],
      report: { enabled: true, icon: false, wording: 'Report Ad', position: 'bottom-right' }
    },
    adsenseConfig: { 'data-ad-slot': '3682385984', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-198', product: 'pau' },
    aditudeConfig: { baseDivId: 'pb-slot-sidebar_bottom', sizes: ADITUDE_SIDEBAR_SIZES },
    sizes: [[300, 250]],
    sizeOverrides: {
      tds: [[300, 600]],
      nitropay: [
        [300, 600],
        [300, 250]
      ]
    },
    providerOverrides: { mediavine: { hidden: true } },
    defaultUrl: _.sample<string>(['/images/site/placehold-300x250.jpg'])!,
    zoneBlocker: { width: 300, height: 445, layout: 'portrait' },
    zoneBlockerGroup: 'sidebar-tall',
    zoneBlockerPriority: 2
  },
  skin: {
    units: [],
    playwireUnitId: '',
    freestarUnitId: '',
    venatusUnitId: '64ca7a1dbab7e94502ecfb51', // 1x1
    egUnitId: 'div-gpt-ad-rdr-0',
    sizes: [[1, 1]],
    defaultUrl: ''
  },
  richmedia_outstream: {
    units: [],
    playwireUnitId: '',
    freestarUnitId: '',
    venatusUnitId: '64cb9f5dbab7e94502ecfbae', // 1x1
    egUnitId: 'div-gpt-ad-rdr-0',
    sizes: [[1, 1]],
    defaultUrl: ''
  },
  venatus_mobile_btf: {
    units: [],
    playwireUnitId: '',
    freestarUnitId: '',
    venatusUnitId: '64ca7b377e562712d6a338b4', // 300x250, 336x280 and 300x600
    egUnitId: '',
    sizes: [[336, 280]],
    defaultUrl: ''
  },
  thin_leaderboard: {
    units: [],
    freestarUnitId: 'raiderio_728x90_468x60_inlist',
    playwireUnitId: 'in_content',
    venatusUnitId: '5e1c4480ddd4900915beaa16', // 728x90 Static - 728x90
    egUnitId: 'div-gpt-ad-rdr-9',
    nitropayUnitId: 'in_article',
    nitropayConfig: {
      demo: FORCE_NITROPAY_DEMO_MODE,
      sizes: [
        ['728', '90'],
        ['970', '90']
      ],
      report: {
        enabled: true,
        icon: false,
        wording: 'Report Ad',
        position: 'bottom-right'
      }
    },
    adsenseConfig: { 'data-ad-slot': '7844992273', 'data-ad-format': 'auto', 'data-full-width-responsive': 'true' },
    tdsConfig: { plId: '11170', prId: '11170-199', product: 'pau' },
    mediavineConfig: { slot: 'Content', sizeMode: 'auto' },
    aditudeConfig: {
      baseDivId: 'pb-slot-thin_leaderboard',
      sizes: { desktop: ADITUDE_HORIZONTAL_SIZES.desktop }
    },
    sizeOverrides: { tds: [[300, 250]] },
    sizes: [[970, 90]],
    defaultUrl: _.sample<string>(['/images/site/placehold-728x90.jpg'])!
  }
};

type TVenatusProps = {
  id: string;
  type: string;
  destroyOnUnmount?: boolean; // when set we do not remove the unit until page change
};

// desired behavior:
// outstream->instream let the mount/unmount happen which willchange which unit is on screen
// instream->instream just re-add the unit

function VenatusPlacement(props: TVenatusProps) {
  const { id } = props;

  const location = useLocation();

  const adRef = useRef(null);

  const isTakeoverUnit = props.type === 'header';

  const { isInStream, wasInStream } = useContext(InStreamVideoContext);

  const dependencies: any[] = [props.type, id];
  if (props.type === 'skin' || props.type === 'richmedia_outstream') {
    if (wasInStream === isInStream) {
      dependencies.push(location);
    }
  } else {
    dependencies.push(location);
  }

  useEffect(() => {
    console.info(`[VENATUS] mounting id ${id} (type: ${props.type})`);
    const domNode = adRef.current; // save this here as `current` is mutable

    if (domNode) {
      console.info(`[VENATUS] adding id ${id} (type: ${props.type})`);
      const addQueue = ((window as any).__vm_add = (window as any).__vm_add || []);
      addQueue.push(domNode);
    } else {
      console.info(`[VENATUS] no current ad node to add. id ${id} (type: ${props.type})`);
    }

    return () => {
      console.info(`[VENATUS] unmounting id ${id} (type: ${props.type})`);

      // venatus would like us to remove richmedia_all category BEFORE adding anything on a new page change
      if (isTakeoverUnit) {
        console.info(`[VENATUS] removing richmedia_all (type: ${props.type})`);
        const removeCategoryQueue = ((window as any).__vm_remove_category = (window as any).__vm_remove_category || []);
        removeCategoryQueue.push('richmedia_all');
      }

      if (props.type === 'skin' || props.type === 'richmedia_outstream') {
        // skin units (the 1x1) never gets removed as per Venatus integration recommendation
        // console.info(`[VENATUS] removing richmedia due to unmounting of unit type ${props.type} (${id})`);
        const removeCategoryQueue = ((window as any).__vm_remove_category = (window as any).__vm_remove_category || []);
        removeCategoryQueue.push('richmedia_all');
        return;
      }

      if (domNode) {
        if (typeof props.destroyOnUnmount !== 'undefined' && !props.destroyOnUnmount) {
          // deferred removal
          console.info(`[VENATUS] deferring removal of id ${id} (type: ${props.type})`);
          const deferredRemovalQueue = ((window as any).__vm_remove_deferred_raiderio =
            (window as any).__vm_remove_deferred_raiderio || []);
          deferredRemovalQueue.push(domNode);
        } else {
          // standard removal
          console.info(`[VENATUS] removing id ${id} (type: ${props.type})`);
          const removeQueue = ((window as any).__vm_remove = (window as any).__vm_remove || []);
          removeQueue.push(domNode);
        }
      } else {
        console.info(`[VENATUS] no current ad node to remove. id ${id} (type: ${props.type})`);
      }
    };
  }, dependencies);

  if (!id) {
    console.warn(`[VENATUS] missing id for this node: ${props.type}`);
    return null;
  }

  return (
    <div
      className="vm-placement"
      data-id={id}
      data-display-type={isTakeoverUnit ? 'hybrid-banner' : undefined}
      ref={adRef}
    />
  );
}

type TPlaywirePlacementProps = { id: string; uniqueId: number };

function PlaywirePlacement(props: TPlaywirePlacementProps) {
  useEffect(() => {
    return () => {
      const { ramp } = window as any;
      if (ramp?.destroyUnits) {
        const selectorId = `pw-zone-id-${props.uniqueId}`;
        const hackRemappedUnitId = PW_HACK_MAP_SELECTOR_ID_TO_UNIT_ID[selectorId];
        const unitIdToDestroy = hackRemappedUnitId || props.id;
        if (hackRemappedUnitId) {
          // clean up so the array doesn't just keep growing
          delete PW_HACK_MAP_SELECTOR_ID_TO_UNIT_ID[selectorId];
        }
        ramp.destroyUnits([unitIdToDestroy]);
      }
    };
  }, [props.id]);

  return <div id={`pw-zone-id-${props.uniqueId}`} className={`pw-${props.id}`} />;
}

type TNitroPlacementProps = {
  id: string;
  nitropayConfig: any;
  zone: any;
};

let ACTIVE_NITROPAY_ADS: any[] = [];

function NitroPlacement({ id, nitropayConfig, zone }: TNitroPlacementProps) {
  const adRef = useRef<HTMLDivElement>(null);
  const nitropayHandleRef = useRef<any>(null);
  const location = useLocation();

  useEffect(() => {
    console.info('====> NitroPlacement mounting', id);
    const { nitroAds } = window as any;
    if (nitroAds && nitropayConfig) {
      Promise.resolve(nitroAds.createAd(id, nitropayConfig)).then((res) => {
        nitropayHandleRef.current = res;
        if (nitropayHandleRef.current) {
          console.info('====> NITRO mounted', nitropayHandleRef.current.id);
          ACTIVE_NITROPAY_ADS.push(nitropayHandleRef.current);
        }
      });
    }

    // Cleanup function (equivalent to componentWillUnmount)
    return () => {
      if (nitropayHandleRef.current) {
        console.info('====> NITRO unmounting', nitropayHandleRef.current.id);
        ACTIVE_NITROPAY_ADS = ACTIVE_NITROPAY_ADS.filter((handle: any) => handle !== nitropayHandleRef.current);
        nitropayHandleRef.current = null;
      }
    };
  }, [id, nitropayConfig, location]);

  if (!id || zone.skipTag) {
    return null;
  }

  return <div id={id} ref={adRef}></div>;
}

type TAdsensePlacementProps = { id: string; adsenseConfig: any; zone: any };

function AdsensePlacement(props: TAdsensePlacementProps) {
  useEffect(() => {
    const { adsbygoogle } = window as any;
    if (adsbygoogle && props.adsenseConfig) {
      try {
        // Google's tag throws synchronously (e.g. "No slot size for availableWidth=0")
        // when the container has zero available width, such as in QR-launched in-app
        // webviews before layout settles. A failing ad slot must never crash the page.
        adsbygoogle.push({});
      } catch (err) {
        frontendLog.error({ err }, 'AdsensePlacement: adsbygoogle.push failed');
      }
    }
  }, [props.id]);

  const { id, zone } = props;
  if (!id) {
    return null;
  }

  const config = { ...BASE_ADSENSE_CONFIG, ...props.adsenseConfig };

  return (
    <ins
      className="adsbygoogle"
      style={{ display: 'block', width: zone.sizes[0][0], height: zone.sizes[0][1] }}
      {...config}
    />
  );
}

type TdsAdPlacementProps = { id: string; tdsConfig: any; zone: any };

function TdsAdPlacement(props: TdsAdPlacementProps) {
  useLayoutEffect(() => {
    if (canUseDOM) {
      if ((window as any).defineRecWidget) {
        (window as any).defineRecWidget({ containerId: props.id, ...props.tdsConfig });
      } else {
        (window as any).recWait = (window as any).recWait || [];
        (window as any).recWait.push({ containerId: props.id, ...props.tdsConfig });
      }
    }
  }, [props.id]);

  const { id, zone } = props;
  if (!id) {
    return null;
  }

  const sizes = zone.sizes[0];
  return (
    <div
      className={`tds-ad tds-${props.tdsConfig.product}-${props.tdsConfig.prId}}`}
      style={{ display: 'block', width: sizes[0], height: sizes[1] }}
      id={props.id}
    />
  );
}

type TMediaVinePlacementProps = { config: TMediaVineConfig; uniqueId: number; hintSlotSizes?: string };

function MediaVinePlacement({ config, uniqueId, hintSlotSizes }: TMediaVinePlacementProps) {
  // Mediavine's script automatically detects mv_slot_target divs and fills them
  return (
    <div
      id={`rio-mv-${uniqueId}`}
      className="mv_slot_target"
      data-slot={config.slot}
      data-hint-slot-sizes={hintSlotSizes}
    />
  );
}

type TAditudePlacementProps = { config: TAditudeConfig; uniqueId: number };

function AditudePlacement({ config, uniqueId }: TAditudePlacementProps) {
  return (
    <div
      className={`rio-aditude-${config.baseDivId}`}
      id={`rio-aditude-${uniqueId}`}
      data-aditude-base-div-id={config.baseDivId}
    />
  );
}

type TFreestarPlacementProps = {
  placementName: string;
  uniqueId: number;
  channel?: string;
};

function FreestarPlacement({ placementName, uniqueId, channel }: TFreestarPlacementProps) {
  return (
    <FreestarAdSlot
      publisher="raider-io"
      placementName={placementName}
      slotId={`freestar-${uniqueId}`}
      channel={channel}
      onNewAdSlotsHook={(name: string) => console.info('[FREESTAR] creating ad', name)}
      onDeleteAdSlotsHook={(name: string) => console.info('[FREESTAR] destroying ad', name)}
    />
  );
}

const REMAPPED_UNIT_ID: Record<string, string> = {
  flex_leaderboard: 'pw-oop-flex_leaderboard',
  site_skin: 'pw-oop-site_skin'
};

export const Zone = (props: TZoneProps) => {
  const { type, hidden, destroyOnUnmount } = props;
  const isClient = useIsClient();
  const siteConfig = useSiteConfig();
  const { displayAdsProvider, videoAdsProvider } = siteConfig as {
    displayAdsProvider: TDisplayAdsProvider;
    videoAdsProvider: TVideoAdsProvider;
  };
  const zoneDefinition = ensure(ZONE_DEFINITIONS[type], 'Expected zone to exist');
  const adsProvider = zoneDefinition.providerSource === 'video' ? videoAdsProvider : displayAdsProvider;
  const { isBlocked: adsBlocked } = useAdblockDetection();
  const adsAreBlocked = adsBlocked === true;

  const [uniqueId] = useState(() => ++uniqueZoneId);

  const state = useMemo(() => {
    const zone = zoneDefinition;

    if (zone.providerOverrides?.[adsProvider]) {
      Object.assign(zone, zone.providerOverrides[adsProvider]);
    }

    if (zone.sizeOverrides?.[adsProvider]) {
      zone.sizes = zone.sizeOverrides[adsProvider];
    }

    return {
      zone,
      unitId:
        (zone as any)[`${adsProvider}UnitId`] ||
        (zone as any)[`${adsProvider}Config`]?.['data-ad-slot'] ||
        (adsProvider === 'aditude' ? zone.aditudeConfig?.baseDivId : undefined) ||
        _.sample(zone.units) ||
        ''
    };
  }, [adsProvider, zoneDefinition]);

  useEffect(() => {
    const { ramp, rioPendingZones, rioRefreshZones } = window as any;
    const aditudeDivId = `rio-aditude-${uniqueId}`;
    let aditudeRegistered = false;

    switch (adsProvider) {
      case 'playwire': {
        if (ramp) {
          ramp.que.push(() => {
            // this must be queued because ramp may not be fully initialized yet
            rioPendingZones.push({
              provider: 'playwire',
              zoneId: state.zone.playwireUnitId,
              selectorId: `pw-zone-id-${uniqueId}`
            });

            if (refreshHandle) {
              clearTimeout(refreshHandle);
            }

            // wait till next frame before making ad request, in case more zones load
            refreshHandle = setTimeout(rioRefreshZones, 16);
          });
        }
        break;
      }
      case 'venatus': {
        // as per their documentation, refresh is handled automatically
        break;
      }
      case 'nitropay': {
        // nitropay - nothing
        break;
      }
      case 'freestar':
      case 'adsense':
      case 'tds':
      case 'mediavine':
        // These providers handle their own lifecycle or auto-discover ad slots
        break;
      case 'aditude': {
        // The placement does not exist until useIsClient flips after hydration.
        // Queue it only from an effect belonging to a render that committed the real div.
        if (
          isClient &&
          !hidden &&
          !adsAreBlocked &&
          !state.zone.hidden &&
          state.zone.aditudeConfig &&
          document.getElementById(aditudeDivId)
        ) {
          queueAditudeZone(state.zone.aditudeConfig.baseDivId, aditudeDivId);
          aditudeRegistered = true;

          if (refreshHandle) {
            clearTimeout(refreshHandle);
          }

          // wait till next frame before making ad request, in case more zones load
          refreshHandle = setTimeout(rioRefreshZones, 16);
        }
        break;
      }
    }

    return () => {
      if (adsProvider === 'aditude' && aditudeRegistered) {
        destroyAditudeSlots([aditudeDivId]);
      }

      if (destroyOnUnmount) {
        if (adsProvider === 'playwire' && ramp?.destroyUnits) {
          const selectorId = `pw-zone-id-${uniqueId}`;
          const hackRemappedUnitId = PW_HACK_MAP_SELECTOR_ID_TO_UNIT_ID[selectorId];
          const unitIdToDestroy = hackRemappedUnitId || REMAPPED_UNIT_ID[state.unitId] || state.unitId;
          if (hackRemappedUnitId) {
            // clean up so the array doesn't just keep growing
            delete PW_HACK_MAP_SELECTOR_ID_TO_UNIT_ID[selectorId];
          }
          ramp.destroyUnits(unitIdToDestroy);
        } else if (adsProvider === 'nitropay') {
          if (state.zone.nitropayConfig.type === 'video-nc') {
            // instream units do not need to be removed from DOM
            return;
          }

          console.info('====> NITRO removing from DOM:', state.unitId);
          const elem = document.getElementById(state.unitId);
          elem?.remove();
        }
      }
    };
  }, [adsAreBlocked, adsProvider, destroyOnUnmount, hidden, isClient, state, uniqueId]);

  if (hidden) {
    return null;
  }

  // Check adblock before provider-hidden so ZoneBlockerPlea can render in hidden zones
  if (adsAreBlocked) {
    if (state.zone.zoneBlocker) {
      return <ZoneBlockerPlea {...state.zone.zoneBlocker} />;
    }

    return null;
  }

  if (state.zone.hidden) {
    return <div id={state.unitId} />;
  }

  if (!isClient) {
    return <Skeleton animate="v2" width={state.zone.sizes[0][0]} height={state.zone.sizes[0][1]} />;
  }

  const sizes = state.zone.sizes;

  const paramsForProvider =
    adsProvider === 'playwire'
      ? { 'data-rio-selector-id': `pw-zone-id-${uniqueId}` }
      : adsProvider === 'aditude'
        ? { 'data-rio-div-id': `rio-aditude-${uniqueId}` }
        : {};

  let placement: ReactElement | null = null;
  switch (adsProvider) {
    case 'playwire':
      placement = <PlaywirePlacement id={state.unitId} uniqueId={uniqueId} />;
      break;
    case 'venatus':
      placement = <VenatusPlacement type={type} id={state.unitId} destroyOnUnmount={destroyOnUnmount} />;
      break;
    case 'nitropay':
      placement = <NitroPlacement id={state.unitId} nitropayConfig={state.zone.nitropayConfig} zone={state.zone} />;
      break;
    case 'adsense':
      placement = <AdsensePlacement id={state.unitId} adsenseConfig={state.zone.adsenseConfig} zone={state.zone} />;
      break;
    case 'tds':
      if (!state.zone.tdsConfig) {
        console.error('TDS zone has no tdsConfig', state.zone);
        return null;
      }
      placement = (
        <TdsAdPlacement id={`tds-ad-${uniqueId.toString()}`} tdsConfig={state.zone.tdsConfig} zone={state.zone} />
      );
      break;
    case 'mediavine':
      if (!state.zone.mediavineConfig) {
        console.warn('MediaVine zone has no mediavineConfig', state.zone);
        return null;
      }
      placement = (
        <MediaVinePlacement
          config={state.zone.mediavineConfig}
          uniqueId={uniqueId}
          hintSlotSizes={buildMediavineHintSizes({
            reservedWidth: sizes[0][0],
            reservedHeight: sizes[0][1],
            mode: state.zone.mediavineConfig.sizeMode,
            manualSizes: state.zone.mediavineConfig.sizes
          })}
        />
      );
      break;
    case 'aditude':
      if (!state.zone.aditudeConfig) {
        console.warn('Aditude zone has no aditudeConfig', state.zone);
        return null;
      }
      placement = <AditudePlacement config={state.zone.aditudeConfig} uniqueId={uniqueId} />;
      break;
    case 'freestar':
      if (!state.zone.freestarUnitId) {
        console.warn('Freestar zone has no freestarUnitId', state.zone);
        return null;
      }
      placement = <FreestarPlacement placementName={state.zone.freestarUnitId} uniqueId={uniqueId} />;
      break;
    default:
      placement = <img src={asset(state.zone.defaultUrl)} width={sizes[0][0]} height={sizes[0][1]} />;
      break;
  }

  if (state.zone.skipTag) {
    // just return the placement without the wrapper
    return placement;
  }

  return (
    <div
      {...paramsForProvider}
      data-rio-unit-id={state.unitId}
      className="rio-zone--wrapper rio-zone"
      style={{ minHeight: sizes[0][1] }}
      id={`rio-zone-${uniqueId}`}
    >
      {placement}
    </div>
  );
};
